import clsx from 'clsx';
import RoundButton from 'Components/RoundButton';
import TipContent from 'Components/Tooltip';
import { exitFullMode } from 'Components/VideoControls/helper';
import { Box, Button, Tip } from 'grommet';
import { LoaderCircle } from 'lucide-preact';
import { useEffect } from 'preact/hooks';
import { connect, useDispatch } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import { logGAEvent } from 'Shared/ga';
import useFetch from 'use-http';

import { getDesktopOrMobile } from '@/helpers';
import { usePendingUGC } from '@/hooks';
import { setClipData, setIsCreatingUGC } from '@/redux/actions';
import { setClipButtonClicked } from '@/redux/slices/user/actions';
import { showTrimVideoModal } from '@/routes/habitat/components/TrimVideoModal/actions';

import { captureClipCreditCheckMessage } from '../../actions';
import clip from './clip.svg';
import style from './style.scss';

const RecordVideoButton = ({
  isClicked,
  dailyCredits,
  plain,
  streamId,
  slug,
  fullScreen,
  setClipDataAction,
  showTrimVideoModalAction,
}) => {
  const dispatch = useDispatch();
  const isPendingUGC = usePendingUGC();
  const { loading, data, error, post } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
    headers: { 'Content-Type': 'application/json' },
    timeout: 300000,
  });
  const device = getDesktopOrMobile();

  useEffect(() => {
    if (data?.videoURL && data?.duration) {
      logGAEvent('clip-button', 'clip-received', slug);
      setClipDataAction({ ...data, loading: false });
      showTrimVideoModalAction();
      dispatch(setIsCreatingUGC(false));
      if (fullScreen) {
        exitFullMode();
      }
    }

    if (error) {
      console.error('Error While requesting stream clip', error);
      logGAEvent('clip-button', 'clip-error', slug);
      dispatch(setIsCreatingUGC(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, dispatch]);

  const onClickHandler = () => {
    if (isPendingUGC) {
      return;
    }

    if (dailyCredits?.inUse && dailyCredits.captureClip === 0) {
      captureClipCreditCheckMessage(dailyCredits, dispatch);
      return;
    }

    setClipDataAction({ loading: true });
    dispatch(setIsCreatingUGC(true));
    post('/videos/clip', { streamId });
    logGAEvent('clip-button', 'click', slug);
    dispatch(setClipButtonClicked(true));
  };

  if (plain) {
    return (
      <Button plain onClick={onClickHandler} className={style.mobileButton} disabled={loading || isPendingUGC}>
        {loading ? <LoaderCircle className="animate-spin" color="#fff" size={24} /> : <img src={clip} alt="Take a clip" />}
      </Button>
    );
  }

  if (device === 'mobile') {
    <Box>
      <RoundButton
        onClick={onClickHandler}
        width="36"
        backgroundColor="var(--blueDark)"
        color="white"
        className={clsx(style.button, { [style.animate]: !isClicked })}
        disabled={loading || isPendingUGC}
      >
        <div className={style.innerContainerClipButton}>
          {loading ? (
            <LoaderCircle className="animate-spin" color="white" size={24} />
          ) : (
            <img src={clip} alt="Take a clip" />
          )}
        </div>
      </RoundButton>
    </Box>;
  }

  return (
    <Tip dropProps={{ align: { left: 'right' } }} content={<TipContent message="Take a Clip" />} plain>
      <Box>
        <RoundButton
          onClick={onClickHandler}
          width="36"
          backgroundColor="var(--blueDark)"
          color="white"
          className={clsx(style.button, { [style.animate]: !isClicked })}
          disabled={loading || isPendingUGC}
        >
          <div className={style.innerContainerClipButton}>
            {loading ? (
              <LoaderCircle className="animate-spin" color="white" size={24} />
            ) : (
              <img src={clip} alt="Take a clip" />
            )}
          </div>
        </RoundButton>
      </Box>
    </Tip>
  );
};

export default connect(
  ({
    user: { clipButtonClicked: isClicked, dailyCredits },
    habitat: {
      habitatInfo: { selectedCamera: { streamKey } = {}, slug: habitatSlug, zoo: { slug: zooSlug } = {} },
    },
    mainStream: { fullScreen },
  }) => ({
    fullScreen,
    isClicked,
    dailyCredits,
    streamId: streamKey,
    slug: `${zooSlug}/${habitatSlug}`,
  }),
  {
    setClipDataAction: setClipData,
    showTrimVideoModalAction: showTrimVideoModal,
  },
)(RecordVideoButton);
